<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import dayjs, { Dayjs } from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

import ErrorModal from '@/components/common/ErrorModal.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SparkBreadcrumb from '@/components/generic/SparkBreadcrumb.vue'
import { ROUTE_NAMES } from '@/router/route-names'
import { useMainStore } from '@/stores'
import { useIntradayStore } from '@/stores/intraday'
import { components } from '#/types/capi-generated'

import { generateId } from './utils'

type Dataset = 'contracts' | 'arbs' | 'breakevens'
type ReleaseFilter = 'live' | 'historical'

const datasetOptions: { text: string; value: Dataset; description: string }[] = [
  { text: 'Contracts', value: 'contracts', description: 'Intraday contract curves' },
  { text: 'Arbs', value: 'arbs', description: 'Intraday arbitrage curves' },
  { text: 'Breakevens', value: 'breakevens', description: 'Intraday breakeven curves' }
]

const releaseOptions: { text: string; value: ReleaseFilter }[] = [
  { text: 'Latest', value: 'live' },
  { text: 'Historical', value: 'historical' }
]

type WideUnit = components['schemas']['commercial_api__views__intraday__v1_0__contracts__common__IntradayContractUnit']

const unitOptions: { text: string; value: WideUnit }[] = [
  { text: '$/MMBtu', value: 'usd-per-mmbtu' },
  { text: '€/MWh', value: 'eur-per-mwh' }
]

const contractOptions: { text: string; value: components['schemas']['IntradayContract'] }[] = [
  { text: 'JKM-TTF', value: 'jkm-ttf' },
  { text: 'TTF', value: 'ttf' }
]

const breakevenTypeOptions: { text: string; value: components['schemas']['CAPIIntradayBreakevenType'] }[] = [
  { text: 'JKM-TTF', value: 'jkm-ttf' },
  { text: 'TTF', value: 'ttf' },
  { text: 'JKM', value: 'jkm' }
]

const percentHireOptions: { text: string; value: components['schemas']['IntradayPercentageEnum'] }[] = [
  { text: '100%', value: '100' },
  { text: '0%', value: '0' }
]

const startDate = ref<Dayjs>(dayjs().subtract(7, 'day'))
const endDate = ref<Dayjs>(dayjs())

const state = reactive<{
  dataset: Dataset
  releaseFilter: ReleaseFilter
  unit: WideUnit
  contract: components['schemas']['IntradayContract']
  breakevenType: components['schemas']['CAPIIntradayBreakevenType']
  fobPortKey: string
  percentHire: components['schemas']['IntradayPercentageEnum']
  extendedCurve: boolean
  submitting: boolean
  errors: string[]
  showErrorModal: boolean
}>({
  dataset: 'contracts',
  releaseFilter: 'live',
  unit: 'usd-per-mmbtu',
  contract: 'jkm-ttf',
  breakevenType: 'jkm-ttf',
  fobPortKey: '',
  percentHire: '100',
  extendedCurve: false,
  submitting: false,
  errors: [],
  showErrorModal: false
})

function fobPortKey(uuid: string, viaPoint: components['schemas']['PointEnum'] | null) {
  return `${uuid}|${viaPoint ?? ''}`
}

function parseFobPortKey(key: string): {
  uuid: string
  viaPoint: components['schemas']['PointEnum'] | null
} {
  const [uuid, viaPointStr] = key.split('|')
  return {
    uuid,
    viaPoint: (viaPointStr || null) as components['schemas']['PointEnum'] | null,
  }
}

const intradayStore = useIntradayStore()
const mainStore = useMainStore()

const needsFobPort = computed(() => state.dataset === 'arbs' || state.dataset === 'breakevens')

const availableUnitOptions = computed(() =>
  needsFobPort.value ? unitOptions.filter((o) => o.value === 'usd-per-mmbtu') : unitOptions
)

watch(needsFobPort, (val) => {
  if (val && state.unit !== 'usd-per-mmbtu') {
    state.unit = 'usd-per-mmbtu'
  }
})

const VIA_POINT_LABELS: Record<components['schemas']['PointEnum'], string> = {
  panama: 'Panama',
  suez: 'Suez',
  cogh: 'COGH',
  'magellan-straits': 'Magellan Straits'
}

function formatFobPortLabel(port: { name: string; viaPoint: components['schemas']['PointEnum'] | null }) {
  return port.viaPoint ? `${port.name} via ${VIA_POINT_LABELS[port.viaPoint]}` : port.name
}

const fobPorts = computed(() =>
  state.dataset === 'breakevens'
    ? intradayStore.breakevensFobPorts
    : intradayStore.arbsFobPorts
)

async function loadFobPorts() {
  if (!needsFobPort.value) return
  const kind = state.dataset === 'breakevens' ? 'breakevens' : 'arbs'
  const ports = await intradayStore.fetchFobPortReferenceData(kind)
  if (
    ports.length > 0 &&
    !ports.find((p) => fobPortKey(p.uuid, p.viaPoint) === state.fobPortKey)
  ) {
    state.fobPortKey = fobPortKey(ports[0].uuid, ports[0].viaPoint)
  }
}

watch(() => state.dataset, loadFobPorts)
onMounted(loadFobPorts)

const MAX_CELLS_PER_OPERATION = 1000000

async function loadData() {
  state.submitting = true
  state.errors = []

  let columns: string[] = []
  let rows: (string | number)[][] = []

  try {
    const start = startDate.value.format('YYYY-MM-DD')
    const end = endDate.value.format('YYYY-MM-DD')

    if (needsFobPort.value && !state.fobPortKey) {
      throw new Error('Please select a FOB Port')
    }

    const { uuid: fobPortUuid, viaPoint } = parseFobPortKey(state.fobPortKey)
    const advanced = {
      percentHire: state.percentHire,
      extendedCurve: state.extendedCurve
    }

    if (state.dataset === 'contracts') {
      const result =
        state.releaseFilter === 'live'
          ? await intradayStore.fetchContractsLive(state.contract, state.unit)
          : await intradayStore.fetchContractsHistorical(state.contract, state.unit, start, end)
      columns = result.columns
      rows = result.rows
    } else if (state.dataset === 'arbs') {
      const narrowUnit: components['schemas']['IntradayUnit'] = 'usd-per-mmbtu'
      const result =
        state.releaseFilter === 'live'
          ? await intradayStore.fetchArbsLive(narrowUnit, fobPortUuid, viaPoint, advanced)
          : await intradayStore.fetchArbsHistorical(narrowUnit, fobPortUuid, start, end, viaPoint, advanced)
      columns = result.columns
      rows = result.rows
    } else if (state.dataset === 'breakevens') {
      const narrowUnit: components['schemas']['IntradayUnit'] = 'usd-per-mmbtu'
      const result =
        state.releaseFilter === 'live'
          ? await intradayStore.fetchBreakevensLive(
              narrowUnit,
              state.breakevenType,
              fobPortUuid,
              viaPoint,
              advanced
            )
          : await intradayStore.fetchBreakevensHistorical(
              narrowUnit,
              state.breakevenType,
              fobPortUuid,
              start,
              end,
              viaPoint,
              advanced
            )
      columns = result.columns
      rows = result.rows
    }

    if (columns.length === 0 || rows.length === 0) {
      throw new Error('No data returned for the selected parameters')
    }

    pasteTableOntoExcel(columns, rows)
  } catch (err: unknown) {
    if (err instanceof Error) {
      state.errors.push(err.message)
    } else {
      state.errors.push('An unknown error occurred')
    }
  } finally {
    if (state.errors.length > 0) {
      state.showErrorModal = true
    }
    state.submitting = false
  }
}

function pasteTableOntoExcel(columns: string[], rows: (string | number)[][]) {
  try {
    if (!window.Excel) {
      console.warn('Excel is not loaded')
      return
    }

    const COLUMNS_COUNT = columns.length
    const ROWS_COUNT = rows.length
    const totalCells = ROWS_COUNT * COLUMNS_COUNT

    if (totalCells > MAX_CELLS_PER_OPERATION) {
      console.warn(`Large dataset detected: ${totalCells} cells.`)
    }

    window.Excel.run(async (context) => {
      const app = context.workbook.application
      app.suspendScreenUpdatingUntilNextSync()
      app.suspendApiCalculationUntilNextSync()

      const workbook = context.workbook
      const sheet = workbook.worksheets.getActiveWorksheet()
      const activeRange = workbook.getSelectedRange()
      const firstCell = activeRange.getCell(0, 0)

      sheet.load('tables')
      activeRange.load('address')
      await context.sync()

      const existingTableCollection = sheet.tables
      const existingTables = existingTableCollection.items

      const tableDataRange = firstCell.getAbsoluteResizedRange(
        ROWS_COUNT + 1,
        COLUMNS_COUNT
      )

      if (existingTables && existingTables.length > 0) {
        const tablesToDelete: string[] = []
        const tableIntersections: { table: Excel.Table; intersection: Excel.Range }[] = []

        for (const existingTable of existingTables) {
          const range = existingTable.getRange()
          const intersection = range.getIntersectionOrNullObject(tableDataRange)
          intersection.load('address')
          tableIntersections.push({ table: existingTable, intersection })
        }

        await context.sync()

        for (const { table, intersection } of tableIntersections) {
          if (!intersection.isNullObject) {
            tablesToDelete.push(table.name)
          }
        }

        for (const tableName of tablesToDelete) {
          try {
            sheet.tables.getItem(tableName).delete()
          } catch (err) {
            state.errors.push(`Error deleting table ${tableName}: ${JSON.stringify(err)}`)
          }
        }

        if (tablesToDelete.length > 0) {
          await context.sync()
        }
      }

      const allTableData = [columns, ...rows]
      tableDataRange.values = allTableData

      const TABLE_NAME = 'IntradayTable' + generateId()
      const dataTable = sheet.tables.add(tableDataRange, true)
      dataTable.name = TABLE_NAME

      const headerRange = dataTable.getHeaderRowRange()
      const dataRange = dataTable.getDataBodyRange()

      dataTable.style = 'TableStyleLight1'

      headerRange.format.fill.color = '#577fa2'
      const headerFont = headerRange.format.font
      headerFont.color = '#FFFFFF'
      headerFont.size = 12
      headerFont.name = 'Mont'
      headerFont.bold = true

      dataRange.format.fill.color = '#FFFFFF'
      const dataFont = dataRange.format.font
      dataFont.color = '#000000'
      dataFont.name = 'Roboto'
      dataFont.bold = false

      if (Office.context.requirements.isSetSupported('ExcelApi', '1.2')) {
        const usedRange = sheet.getUsedRange()
        const usedRangeFormat = usedRange.format
        usedRangeFormat.autofitColumns()
        usedRangeFormat.autofitRows()
      }

      sheet.activate()
      await context.sync()
    }).catch(async (error) => {
      console.error('Error in pasteTableOntoExcel Excel.run:', error)
      throw error
    })
  } catch (err: any) {
    state.errors.push(err.message)
  } finally {
    state.submitting = false
  }
}

const router = useRouter()

function handleBreadcrumbInput(value: string) {
  router.push({ name: value })
}
</script>

<template>
  <Loader :modelValue="mainStore.isLoading">
    <div class="intraday-page p-5 space-y-10">
      <section class="space-y-6">
        <PageHeader :title="`Get Intraday Data`" />
        <SparkBreadcrumb
          :config="[
            { name: 'Products', icon: 'home-filled', value: ROUTE_NAMES.HOME },
            {
              name: 'Intraday',
              icon: 'chartCandlestick',
              styleClasses: `bg-intraday-500 text-white`
            }
          ]"
          @input="handleBreadcrumbInput"
        />
      </section>

      <a-form layout="vertical">
        <a-form-item :colon="false" label="Select Dataset">
          <div class="grid grid-cols-3 gap-3">
            <div
              v-for="opt in datasetOptions"
              :key="opt.value"
              class="data-type-card"
              :class="{ 'data-type-card--selected': state.dataset === opt.value }"
              @click="state.dataset = opt.value"
            >
              <div class="data-type-card__content">
                <span class="data-type-card__title">{{ opt.text }}</span>
                <span class="data-type-card__description">{{ opt.description }}</span>
              </div>
            </div>
          </div>
        </a-form-item>

        <a-form-item v-if="state.dataset === 'contracts'" :colon="false" label="Contract">
          <a-radio-group v-model:value="state.contract" button-style="solid" size="small">
            <a-radio-button v-for="opt in contractOptions" :value="opt.value" :key="opt.value">
              {{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item v-if="state.dataset === 'breakevens'" :colon="false" label="Breakeven Type">
          <a-radio-group v-model:value="state.breakevenType" button-style="solid" size="small">
            <a-radio-button v-for="opt in breakevenTypeOptions" :value="opt.value" :key="opt.value">
              {{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item v-if="needsFobPort" :colon="false" label="FOB Port">
          <a-select
            v-model:value="state.fobPortKey"
            size="small"
            class="w-full"
            placeholder="Select a FOB Port"
            :options="fobPorts.map((p) => ({ label: formatFobPortLabel(p), value: fobPortKey(p.uuid, p.viaPoint) }))"
            show-search
            option-filter-prop="label"
          />
        </a-form-item>

        <a-form-item :colon="false" label="Price Unit">
          <a-radio-group v-model:value="state.unit" button-style="solid" size="small">
            <a-radio-button v-for="opt in availableUnitOptions" :value="opt.value" :key="opt.value">
              {{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item :colon="false" label="Releases">
          <a-radio-group v-model:value="state.releaseFilter" size="small">
            <a-radio-button v-for="opt in releaseOptions" :value="opt.value" :key="opt.value">
              {{ opt.text }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>

        <div v-if="state.releaseFilter === 'historical'">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <a-form-item :colon="false" label="Start Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="startDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
            <div>
              <a-form-item :colon="false" label="End Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="endDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
          </div>
          <p class="text-sm text-amber-600 flex items-center gap-1 mb-4">
            <InfoCircleOutlined />
            <span>Maximum date range is 30 days</span>
          </p>
        </div>

        <div v-if="needsFobPort" class="space-y-3 mb-4">
          <a-form-item :colon="false" label="Percent Hire" class="!mb-1">
            <a-radio-group v-model:value="state.percentHire" button-style="solid" size="small">
              <a-radio-button
                v-for="opt in percentHireOptions"
                :value="opt.value"
                :key="opt.value"
              >
                {{ opt.text }}
              </a-radio-button>
            </a-radio-group>
          </a-form-item>
          <a-form-item :colon="false" class="!mb-1">
            <template #label>
              <span class="flex items-center gap-1.5">
                Extended Curve
                <a-tooltip
                  placement="top"
                  title="Returns an extended forward curve using Spark modelled values for M>11 NWE-TTF differentials. For further details, please check the Intraday platform."
                >
                  <InfoCircleOutlined class="text-gray-400 hover:text-gray-600 cursor-help" />
                </a-tooltip>
              </span>
            </template>
            <a-switch v-model:checked="state.extendedCurve" size="small" />
          </a-form-item>
        </div>

        <div>
          <SparkButton
            class="w-full"
            size="lg"
            color="intraday"
            :loading="state.submitting"
            @click="loadData"
          >
            Insert data into selected cell
          </SparkButton>
        </div>
      </a-form>

      <ErrorModal
        v-model:visible="state.showErrorModal"
        title="Some data could not be retrieved"
        description="The following issues were encountered during data retrieval."
        :errors="state.errors"
      />
    </div>
  </Loader>
</template>

<style lang="scss">
.intraday-page {
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: #577fa2 !important;
    border-color: #577fa2 !important;
    color: #ffffff !important;

    span {
      color: #ffffff !important;
    }

    &:hover {
      background: #436382 !important;
      border-color: #436382 !important;
      color: #ffffff !important;
    }

    &::before {
      background-color: #577fa2 !important;
    }
  }

  .data-type-card {
    @apply p-3 rounded-lg border-2 border-gray-200 cursor-pointer transition-all duration-200;

    &:hover {
      @apply border-intraday-400 bg-intraday-50;
    }

    &--selected {
      @apply border-intraday-500 bg-intraday-50;

      &:hover {
        @apply border-intraday-500;
      }
    }

    &__content {
      @apply flex flex-col gap-0.5;
    }

    &__title {
      @apply font-semibold text-base text-gray-900 leading-tight;
    }

    &__description {
      @apply text-sm text-gray-600 leading-tight;
    }
  }
}
</style>
